// Kept free of imports so the HTTP client can stamp these on every request without
// importing the tracker, which itself sends through the HTTP client's settings.

const SESSION_STORAGE_KEY = "portalActivitySession";
const DEVICE_STORAGE_KEY = "portalActivityDevice";
const CLOCK_OFFSET_STORAGE_KEY = "portalClockOffset";

export const PortalDevice = {
  mobile: "M",
  tablet: "T",
  desktop: "D",
  installedApp: "I",
} as const;

let fallbackSessionId: string | undefined;
let fallbackDevice: { sessionId: string; device: string } | undefined;
let clockOffset: number | undefined;

function newSessionId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }

  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
}

// One id per browser tab: sessionStorage is per tab and survives a reload.
export function getActivitySessionId(): string {
  try {
    let sessionId = sessionStorage.getItem(SESSION_STORAGE_KEY);

    if (!sessionId) {
      sessionId = newSessionId();
      sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId);
    }

    return sessionId;
  } catch {
    fallbackSessionId = fallbackSessionId ?? newSessionId();
    return fallbackSessionId;
  }
}

// A new session begins when someone signs in or out, so two people sharing a tab are
// never recorded as one session.
export function startNewActivitySession() {
  fallbackSessionId = undefined;
  fallbackDevice = undefined;

  try {
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
    sessionStorage.removeItem(DEVICE_STORAGE_KEY);
  } catch {
    // nothing stored, so the next read starts a new one anyway
  }
}

function readJsonWebTokenClaims(token: string | undefined): Record<string, unknown> {
  try {
    const payload = token?.split(".")[1];

    if (!payload) {
      return {};
    }

    // base64url of UTF-8 text, so a login that is not ASCII reads as WebLoginHelpers reads it
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const binary = atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, "="));
    return JSON.parse(new TextDecoder().decode(Uint8Array.from(binary, (character) => character.charCodeAt(0))));
  } catch {
    return {};
  }
}

export function getJsonWebTokenLogin(token: string | undefined): string {
  const login = readJsonWebTokenClaims(token).webLoginId;
  return typeof login === "string" ? login : "";
}

// The server's clock less this device's, learned as each token arrives, so a device
// clock set wrong neither holds a current token back nor sends an expired one. It can
// still go wrong after, as when the clock is changed, so it only ever decides whether
// to hold events, never whether to refresh the token.
export function noteJsonWebTokenReceived(token: string | undefined, receivedAt: number) {
  const claims = readJsonWebTokenClaims(token);
  const issued = typeof claims.iat === "number" ? claims.iat : claims.nbf;

  if (typeof issued !== "number") {
    return;
  }

  clockOffset = issued * 1000 - receivedAt;

  try {
    localStorage.setItem(CLOCK_OFFSET_STORAGE_KEY, String(clockOffset));
  } catch {
    // this tab keeps it; another tab learns its own at its next refresh
  }
}

function getClockOffset(): number {
  if (clockOffset === undefined) {
    try {
      const stored = Number(localStorage.getItem(CLOCK_OFFSET_STORAGE_KEY));
      clockOffset = Number.isFinite(stored) ? stored : 0;
    } catch {
      clockOffset = 0;
    }
  }

  return clockOffset;
}

export function resetClockOffsetForTests() {
  clockOffset = undefined;
}

// A few seconds early, so a send started now does not arrive just after it expires.
export function isJsonWebTokenCurrent(token: string | undefined, now = Date.now()): boolean {
  const expires = readJsonWebTokenClaims(token).exp;
  return typeof expires === "number" && expires * 1000 > now + getClockOffset() + 5000;
}

type DeviceView = Partial<Pick<Window, "innerWidth" | "innerHeight" | "matchMedia">> & {
  screen?: { width: number; height: number };
};

// NaN unless both sides are known.
function shorterSide(width = Number.NaN, height = Number.NaN): number {
  return width > 0 && height > 0 ? Math.min(width, height) : Number.NaN;
}

// A coarse class only; the browser's user agent string is never sent. A touch device is
// judged by its shorter side, so turning a phone or tablet never changes its class, and
// by the screen's before the window's, so a tablet showing two apps is still a tablet.
export function getDeviceClass(view: DeviceView = window): string {
  const matches = (query: string) =>
    typeof view.matchMedia === "function" && view.matchMedia(query).matches;

  if (matches("(display-mode: standalone)")) {
    return PortalDevice.installedApp;
  }

  const width = view.innerWidth ?? Number.NaN;

  if (!matches("(pointer: coarse)")) {
    return width < 600 ? PortalDevice.mobile : PortalDevice.desktop;
  }

  const screenSide = shorterSide(view.screen?.width, view.screen?.height);
  const side = Number.isNaN(screenSide)
    ? shorterSide(view.innerWidth, view.innerHeight)
    : screenSide;

  // A phone's shorter side is under 600 CSS pixels, a tablet's under 1080.
  if (Number.isNaN(side) ? width < 768 : side < 600) {
    return PortalDevice.mobile;
  }

  if (Number.isNaN(side) ? width < 1280 : side < 1080) {
    return PortalDevice.tablet;
  }

  return PortalDevice.desktop;
}

// Judged once per session, so a window resized or a device turned mid-session never
// records one session as two devices.
export function getSessionDeviceClass(view: DeviceView = window): string {
  const sessionId = getActivitySessionId();

  try {
    const [storedSession, storedDevice] = (sessionStorage.getItem(DEVICE_STORAGE_KEY) ?? "").split("|");

    if (storedSession === sessionId && storedDevice) {
      return storedDevice;
    }

    const device = getDeviceClass(view);
    sessionStorage.setItem(DEVICE_STORAGE_KEY, `${sessionId}|${device}`);
    return device;
  } catch {
    if (fallbackDevice?.sessionId !== sessionId) {
      fallbackDevice = { sessionId, device: getDeviceClass(view) };
    }

    return fallbackDevice.device;
  }
}
